import React, { useState, useEffect, useRef } from "react";
import { useAccount, useSignTypedData } from "wagmi";
import { ethers } from "ethers";
import {
  Heading,
  HeadingVariant,
  Text,
  TextVariant,
  TextWeight,
  Chip,
  ChipVariant,
  View,
  Button,
  ButtonVariant,
  ButtonSize,
  Input,
  Label,
  InfoIcon,
  RefreshIcon,
  ExternalLinkIcon,
  joinClasses,
} from "@pufferfinance/puffer-ui-components";
import { CONTRACTS, X402_CONFIG } from "../constants/contracts";
import {
  UNIFI_CHAIN_ID,
  txExplorerUrl,
  AP2_SERVICE_URL,
} from "../constants/network";
import { getReadProvider } from "../utils/provider";
import { useAppToast } from "../hooks/useAppToast";
import styles from "./AutomatedPurchasing.module.scss";

interface AutomatedPurchasingProps {
  onBalanceRefresh: () => void;
}

interface MockItem {
  id: string;
  name: string;
  description: string;
  basePrice: number;
  currentPrice: number;
  priceRange: [number, number];
  merchant: string;
  emoji: string;
}

interface ActiveBid {
  itemId: string;
  bidAmount: number;
  maxPrice: number;
  createdAt: number;
  status: "active" | "executing" | "executed" | "expired" | "cancelled";
  txHash?: string;
  purchasePrice?: number;
  userSignature?: string;
  intentData?: any;
}

const MOCK_ITEMS: MockItem[] = [
  {
    id: "premium-api-access",
    name: "Premium API Access",
    description: "Monthly subscription to premium API features",
    basePrice: 10.0,
    currentPrice: 12.0,
    priceRange: [8.0, 15.0],
    merchant: X402_CONFIG.MERCHANT_ADDRESS,
    emoji: "🚀",
  },
  {
    id: "nft-artwork",
    name: "Digital Artwork NFT",
    description: "Limited edition digital artwork by emerging artist",
    basePrice: 25.0,
    currentPrice: 28.0,
    priceRange: [20.0, 35.0],
    merchant: X402_CONFIG.MERCHANT_ADDRESS,
    emoji: "🎨",
  },
  {
    id: "data-analytics",
    name: "Market Data Package",
    description: "Real-time market analytics and insights",
    basePrice: 5.0,
    currentPrice: 7.0,
    priceRange: [3.0, 9.0],
    merchant: X402_CONFIG.MERCHANT_ADDRESS,
    emoji: "📊",
  },
];

const STATUS_CHIP_VARIANT: Record<string, ChipVariant> = {
  active: ChipVariant.Success,
  executing: ChipVariant.Warning,
  executed: ChipVariant.Primary,
  cancelled: ChipVariant.Danger,
  expired: ChipVariant.Neutral,
};

export const AutomatedPurchasing: React.FC<AutomatedPurchasingProps> = ({
  onBalanceRefresh,
}) => {
  const { showError, showSuccess, showInfo, showWalletError } = useAppToast();
  const { address, isConnected, connector } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();

  const [selectedItem, setSelectedItem] = useState<MockItem | null>(null);
  const [bidAmount, setBidAmount] = useState<string>("");
  const [items, setItems] = useState<MockItem[]>(MOCK_ITEMS);
  const [activeBids, setActiveBids] = useState<ActiveBid[]>([]);
  const [isCreatingBid, setIsCreatingBid] = useState(false);
  const [usdcBalance, setUsdcBalance] = useState<string>("0");
  const [usdcAllowance, setUsdcAllowance] = useState<string>("0");
  const [isApproving, setIsApproving] = useState(false);

  const executingBids = useRef<Set<string>>(new Set());

  // Per-bid retry bookkeeping. When a purchase fails (e.g. the RPC is
  // rate-limited) we must NOT immediately re-fire on the next 2s tick - that
  // hammers the already-overloaded RPC and stacks identical error toasts.
  // Instead we back off exponentially and cap the number of attempts.
  const purchaseRetries = useRef<
    Map<string, { attempts: number; cooldownUntil: number }>
  >(new Map());
  const MAX_PURCHASE_ATTEMPTS = 5;
  const BASE_RETRY_DELAY_MS = 5000;
  const MAX_RETRY_DELAY_MS = 60000;

  const checkUSDCBalance = async () => {
    if (!address || !isConnected) return;

    try {
      const provider = getReadProvider();
      const tokenAbi = [
        "function balanceOf(address) view returns (uint256)",
        "function decimals() view returns (uint8)",
        "function allowance(address owner, address spender) view returns (uint256)",
      ];

      const usdcContract = new ethers.Contract(
        CONTRACTS.USDC,
        tokenAbi,
        provider,
      );
      const balance = await usdcContract.balanceOf(address);
      const allowance = await usdcContract.allowance(
        address,
        CONTRACTS.PAYMENT_FACILITATOR,
      );
      const decimals = await usdcContract.decimals();
      const balanceFormatted = ethers.formatUnits(balance, decimals);
      const allowanceFormatted = ethers.formatUnits(allowance, decimals);

      setUsdcBalance(balanceFormatted);
      setUsdcAllowance(allowanceFormatted);
    } catch (error) {
      console.error("Error checking USDC balance:", error);
      setUsdcBalance("0");
      setUsdcAllowance("0");
    }
  };

  const approveUSDC = async (amount: string) => {
    if (!address || !isConnected || !connector) return;

    try {
      setIsApproving(true);
      showInfo("Requesting USDC approval...");

      // Use the selected wagmi connector's provider (EIP-6963) instead of
      // window.ethereum, which can be hijacked by other installed wallets.
      const eip1193 = (await connector.getProvider()) as ethers.Eip1193Provider;
      const provider = new ethers.BrowserProvider(eip1193);
      const signer = await provider.getSigner();
      const tokenAbi = [
        "function approve(address spender, uint256 amount) returns (bool)",
      ];

      const usdcContract = new ethers.Contract(
        CONTRACTS.USDC,
        tokenAbi,
        signer,
      );
      const amountWei = ethers.parseUnits(amount, 6);

      const tx = await usdcContract.approve(
        CONTRACTS.PAYMENT_FACILITATOR,
        amountWei,
      );
      await tx.wait();

      showSuccess(
        `Successfully approved ${amount} USDC for automated purchasing!`,
      );

      setTimeout(async () => {
        await checkUSDCBalance();
        onBalanceRefresh();
      }, 1000);
    } catch (error) {
      console.error("USDC approval failed:", error);
      showWalletError(error, "USDC approval");
    } finally {
      setIsApproving(false);
    }
  };

  useEffect(() => {
    if (isConnected && address) {
      checkUSDCBalance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, address]);

  useEffect(() => {
    const interval = setInterval(() => {
      setItems((prevItems) =>
        prevItems.map((item) => {
          const [min, max] = item.priceRange;
          const volatility = 0.1;
          const change = (Math.random() - 0.5) * 2 * volatility;
          const newPrice = Math.max(
            min,
            Math.min(max, item.currentPrice * (1 + change)),
          );

          return {
            ...item,
            currentPrice: Number((Math.round(newPrice * 100) / 100).toFixed(2)),
          };
        }),
      );
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const checkBids = () => {
      setActiveBids((prevBids) => {
        let hasChanges = false;
        const updatedBids = prevBids.map((bid) => {
          if (bid.status !== "active") return bid;

          const item = items.find((i) => i.id === bid.itemId);
          if (!item) return bid;

          const currentPrice = Number(item.currentPrice.toFixed(2));
          if (currentPrice <= bid.bidAmount) {
            const bidKey = `${bid.itemId}-${bid.createdAt}`;

            if (executingBids.current.has(bidKey)) {
              return bid;
            }

            // Respect the backoff window after a previous failure so we don't
            // retry on every tick.
            const retry = purchaseRetries.current.get(bidKey);
            if (retry && Date.now() < retry.cooldownUntil) {
              return bid;
            }

            executingBids.current.add(bidKey);
            const updatedBid = { ...bid, status: "executing" as const };
            hasChanges = true;
            setTimeout(() => executePurchase(updatedBid, item), 0);
            return updatedBid;
          }

          if (Date.now() - bid.createdAt > 24 * 60 * 60 * 1000) {
            hasChanges = true;
            return { ...bid, status: "expired" as const };
          }

          return bid;
        });

        return hasChanges ? updatedBids : prevBids;
      });
    };

    const interval = setInterval(checkBids, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const executePurchase = async (bid: ActiveBid, item: MockItem) => {
    if (!address || !isConnected || !bid.userSignature || !bid.intentData)
      return;

    const bidKey = `${bid.itemId}-${bid.createdAt}`;

    if (!executingBids.current.has(bidKey)) {
      return;
    }

    try {
      showInfo(
        `Executing automated purchase for ${item.name} at $${item.currentPrice}...`,
      );

      const intent = bid.intentData;
      const formattedCurrentPrice = Number(item.currentPrice.toFixed(2));

      const cart = {
        merchant: item.merchant,
        token: CONTRACTS.USDC,
        amount: ethers
          .parseUnits(formattedCurrentPrice.toString(), 6)
          .toString(),
      };

      const res = await fetch(`${AP2_SERVICE_URL}/execute-purchase`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          intent,
          cart,
          userSignature: bid.userSignature,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(
          err.error || `Purchase failed (${res.status})`,
        );
      }

      const { txHash } = await res.json();

      showSuccess(
        `Successfully purchased ${item.name} for $${formattedCurrentPrice} USDC! View transaction: ${txExplorerUrl(txHash)}`,
      );
      onBalanceRefresh();
      await checkUSDCBalance();

      executingBids.current.delete(bidKey);
      purchaseRetries.current.delete(bidKey);

      setActiveBids((prevBids) =>
        prevBids.map((b) =>
          b.itemId === bid.itemId && b.createdAt === bid.createdAt
            ? {
                ...b,
                status: "executed" as const,
                txHash,
                purchasePrice: formattedCurrentPrice,
              }
            : b,
        ),
      );
    } catch (error) {
      console.error("Automated purchase failed:", error);

      executingBids.current.delete(bidKey);

      const prev = purchaseRetries.current.get(bidKey);
      const attempts = (prev?.attempts ?? 0) + 1;

      if (attempts >= MAX_PURCHASE_ATTEMPTS) {
        // Give up auto-retrying so we stop spamming the RPC. The bid is marked
        // expired; the user can create a fresh bid to try again.
        showWalletError(error, "Automated purchase");
        showError(
          `Automated purchase for ${item.name} paused after ${MAX_PURCHASE_ATTEMPTS} failed attempts. Please try again later.`,
        );
        purchaseRetries.current.delete(bidKey);

        setActiveBids((prevBids) =>
          prevBids.map((b) =>
            b.itemId === bid.itemId && b.createdAt === bid.createdAt
              ? { ...b, status: "expired" as const }
              : b,
          ),
        );
        return;
      }

      // Exponential backoff before the next attempt. The deduped toast means
      // the user sees the error at most once per window even while we wait.
      const delay = Math.min(
        BASE_RETRY_DELAY_MS * 2 ** (attempts - 1),
        MAX_RETRY_DELAY_MS,
      );
      purchaseRetries.current.set(bidKey, {
        attempts,
        cooldownUntil: Date.now() + delay,
      });
      showWalletError(error, "Automated purchase");

      setActiveBids((prevBids) =>
        prevBids.map((b) =>
          b.itemId === bid.itemId && b.createdAt === bid.createdAt
            ? { ...b, status: "active" as const }
            : b,
        ),
      );
    }
  };

  const createBid = async () => {
    if (!selectedItem || !bidAmount || !address || !isConnected) return;

    const bidValue = parseFloat(bidAmount);
    if (isNaN(bidValue) || bidValue <= 0) {
      showError("Please enter a valid bid amount");
      return;
    }

    const currentBalance = parseFloat(usdcBalance);
    if (currentBalance < bidValue) {
      showError(
        `Insufficient USDC balance. You have ${currentBalance.toFixed(2)} USDC but need ${bidValue.toFixed(2)} USDC for this bid.`,
      );
      return;
    }

    const currentAllowance = parseFloat(usdcAllowance);
    if (currentAllowance < bidValue) {
      showError(
        `Insufficient USDC approval. Current allowance: ${currentAllowance.toFixed(2)} USDC, needed: ${bidValue.toFixed(2)} USDC. Please approve more USDC first.`,
      );
      return;
    }

    const formattedBidValue = Number(bidValue.toFixed(2));

    setIsCreatingBid(true);

    try {
      const nonce = ethers.hexlify(ethers.randomBytes(32));
      const expires = Math.floor(Date.now() / 1000) + 24 * 60 * 60;
      const taskHash = ethers.keccak256(
        ethers.toUtf8Bytes(`automated-purchase-${selectedItem.id}`),
      );
      const maxPriceFormatted = formattedBidValue;

      const intent = {
        user: address,
        task: taskHash,
        token: CONTRACTS.USDC,
        maxPrice: ethers.parseUnits(maxPriceFormatted.toString(), 6).toString(),
        expires,
        nonce,
      };

      const userSignature = await signTypedDataAsync({
        domain: {
          name: "PaymentFacilitator",
          version: "1",
          chainId: UNIFI_CHAIN_ID,
          verifyingContract: CONTRACTS.PAYMENT_FACILITATOR as `0x${string}`,
        },
        types: {
          IntentMandate: [
            { name: "user", type: "address" },
            { name: "task", type: "bytes32" },
            { name: "token", type: "address" },
            { name: "maxPrice", type: "uint256" },
            { name: "expires", type: "uint256" },
            { name: "nonce", type: "uint256" },
          ],
        },
        primaryType: "IntentMandate",
        message: {
          user: intent.user as `0x${string}`,
          task: intent.task as `0x${string}`,
          token: intent.token as `0x${string}`,
          maxPrice: BigInt(intent.maxPrice),
          expires: BigInt(intent.expires),
          nonce: BigInt(intent.nonce),
        },
      });

      const newBid: ActiveBid = {
        itemId: selectedItem.id,
        bidAmount: formattedBidValue,
        maxPrice: maxPriceFormatted,
        createdAt: Date.now(),
        status: "active",
        userSignature,
        intentData: intent,
      };

      const existingBidIndex = activeBids.findIndex(
        (bid) => bid.itemId === selectedItem.id && bid.status === "active",
      );

      if (existingBidIndex !== -1) {
        setActiveBids((prev) =>
          prev.map((bid, index) => (index === existingBidIndex ? newBid : bid)),
        );
        showSuccess(
          `Bid updated for ${selectedItem.name} to $${formattedBidValue} USDC. Previous bid replaced.`,
        );
      } else {
        setActiveBids((prev) => [...prev, newBid]);
        showSuccess(
          `Bid created for ${selectedItem.name} at $${formattedBidValue} USDC. Agent will execute when price is favorable.`,
        );
      }

      setBidAmount("");
      setSelectedItem(null);
    } catch (error) {
      console.error("Bid creation failed:", error);
      showWalletError(error, "Bid creation");
    } finally {
      setIsCreatingBid(false);
    }
  };

  const cancelBid = (bidIndex: number) => {
    const bid = activeBids[bidIndex];
    if (!bid || bid.status !== "active") return;

    setActiveBids((prev) =>
      prev.map((b, index) =>
        index === bidIndex ? { ...b, status: "cancelled" as const } : b,
      ),
    );

    const item = items.find((i) => i.id === bid.itemId);
    showInfo(
      `Cancelled bid for ${item?.name || "item"} at $${bid.bidAmount} USDC`,
    );
  };

  const formatTime = (timestamp: number) =>
    new Date(timestamp).toLocaleTimeString();

  const bidValueNum = parseFloat(bidAmount);
  const insufficientBalance =
    !!bidAmount && bidValueNum > parseFloat(usdcBalance);
  const insufficientAllowance =
    !!bidAmount && bidValueNum > parseFloat(usdcAllowance);
  const activeBidExists =
    !!selectedItem &&
    activeBids.some(
      (bid) => bid.itemId === selectedItem.id && bid.status === "active",
    );

  return (
    <View flexDirection="column" gap={16} className={styles.wrapper}>
      {/* Header */}
      <View flexDirection="column" gap={2}>
        <Heading variant={HeadingVariant.Title3}>Automated Purchasing</Heading>
        <Text variant={TextVariant.Body2} color="var(--secondary-cadet-50)">
          Set a bid - your agent will buy automatically when the price drops.
        </Text>
      </View>

      {!isConnected && (
        <View
          flexDirection="row"
          gap={3}
          alignItems="center"
          className={styles.statusWarning}
        >
          <View className={styles.statusIcon}>
            <InfoIcon size={16} />
          </View>
          <Text variant={TextVariant.Body2} color="var(--primary-navy-90)">
            Please connect your wallet to use automated purchasing.
          </Text>
        </View>
      )}

      {/* Item selection */}
      <View flexDirection="column" gap={6}>
        <Text
          variant={TextVariant.Body2}
          weight={TextWeight.SemiBold}
          color="var(--secondary-cadet-50)"
        >
          Select an item
        </Text>
        <div className={styles.itemGrid}>
          {items.map((item) => {
            const hasActiveBid = activeBids.some(
              (bid) => bid.itemId === item.id && bid.status === "active",
            );
            const isSelected = selectedItem?.id === item.id;
            const cardClass = joinClasses(
              styles.itemCard,
              isSelected && styles.itemCardSelected,
              hasActiveBid && styles.itemCardActive,
            );

            return (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedItem(item)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setSelectedItem(item);
                }}
                className={cardClass}
              >
                {hasActiveBid && (
                  <div className={styles.itemCardBadge}>
                    <Chip variant={ChipVariant.Success}>ACTIVE</Chip>
                  </div>
                )}
                <View
                  flexDirection="row"
                  gap={3}
                  alignItems="center"
                  className={styles.itemCardTop}
                >
                  <span className={styles.itemEmoji}>{item.emoji}</span>
                  <View flexDirection="column">
                    <Text
                      variant={TextVariant.Body1}
                      weight={TextWeight.SemiBold}
                      color="var(--primary-navy-90)"
                    >
                      {item.name}
                    </Text>
                    <Text
                      variant={TextVariant.Caption1}
                      color="var(--secondary-cadet-50)"
                    >
                      {item.description}
                    </Text>
                  </View>
                </View>
                <View
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="flex-end"
                  className={styles.itemCardBottom}
                >
                  <View flexDirection="column">
                    <Text
                      variant={TextVariant.Caption1}
                      color="var(--secondary-cadet-50)"
                    >
                      Current price
                    </Text>
                    <span className={styles.priceValue}>
                      ${item.currentPrice}
                    </span>
                  </View>
                  <Text
                    variant={TextVariant.Caption1}
                    color="var(--secondary-cadet-50)"
                  >
                    Range ${item.priceRange[0]}–${item.priceRange[1]}
                  </Text>
                </View>
              </div>
            );
          })}
        </div>
      </View>

      {/* Bid form */}
      {selectedItem && (
        <View flexDirection="column" gap={6} className={styles.bidForm}>
          <View flexDirection="column" gap={1}>
            <Text
              variant={TextVariant.Body1}
              weight={TextWeight.SemiBold}
              color="var(--primary-navy-90)"
            >
              Create bid for {selectedItem.name}
            </Text>
            <Text
              variant={TextVariant.Caption1}
              color="var(--secondary-cadet-50)"
            >
              Agent will buy when current price ≤ your bid.
            </Text>
          </View>

          {activeBidExists && (
            <View
              flexDirection="row"
              gap={3}
              alignItems="center"
              className={styles.statusWarning}
            >
              <View className={styles.statusIcon}>
                <InfoIcon size={16} />
              </View>
              <Text
                variant={TextVariant.Caption1}
                color="var(--primary-navy-90)"
              >
                You already have an active bid for this item - creating a new
                bid will replace it.
              </Text>
            </View>
          )}

          <View flexDirection="column" gap={3}>
            <Label htmlFor="bidAmount" className={styles.fieldLabel}>
              Bid Amount (USDC)
            </Label>
            <Input
              type="number"
              id="bidAmount"
              name="bidAmount"
              value={bidAmount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setBidAmount(e.target.value)
              }
              placeholder="0.00"
              min={0}
              step={0.01}
              className={joinClasses(
                styles.input,
                (insufficientBalance || insufficientAllowance) &&
                  styles.inputError,
              )}
            />
          </View>

          {/* Meta info */}
          <View flexDirection="column" gap={3} className={styles.metaRows}>
            <View flexDirection="row" justifyContent="space-between">
              <Text
                variant={TextVariant.Caption1}
                color="var(--secondary-cadet-50)"
              >
                Current price
              </Text>
              <Text
                variant={TextVariant.Caption1}
                weight={TextWeight.SemiBold}
                color="var(--primary-navy-90)"
              >
                $
                {items.find((i) => i.id === selectedItem.id)?.currentPrice ??
                  selectedItem.currentPrice}{" "}
                USDC
              </Text>
            </View>
            <View flexDirection="row" justifyContent="space-between">
              <Text
                variant={TextVariant.Caption1}
                color="var(--secondary-cadet-50)"
              >
                Balance
              </Text>
              <Text
                variant={TextVariant.Caption1}
                weight={TextWeight.SemiBold}
                color="var(--primary-navy-90)"
              >
                ${parseFloat(usdcBalance).toFixed(2)} USDC
              </Text>
            </View>
            <View
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Text
                variant={TextVariant.Caption1}
                color="var(--secondary-cadet-50)"
              >
                Approved
              </Text>
              <View flexDirection="row" gap={2} alignItems="center">
                <Text
                  variant={TextVariant.Caption1}
                  weight={TextWeight.SemiBold}
                  color="var(--primary-navy-90)"
                >
                  ${parseFloat(usdcAllowance).toFixed(2)} USDC
                </Text>
                <Button
                  variant={ButtonVariant.Unstyled}
                  onPress={checkUSDCBalance}
                  aria-label="Refresh approved amount"
                  className={styles.iconButton}
                >
                  <RefreshIcon size={14} />
                </Button>
              </View>
            </View>
          </View>

          {/* Inline warnings + approve */}
          {insufficientBalance && (
            <Text variant={TextVariant.Caption1} color="var(--error-60)">
              Insufficient balance for this bid amount.
            </Text>
          )}
          {insufficientAllowance && (
            <View flexDirection="column" gap={3}>
              <Text variant={TextVariant.Caption1} color="var(--error-60)">
                Insufficient USDC approval - approve more to continue.
              </Text>
              <Button
                variant={ButtonVariant.Border}
                size={ButtonSize.Small}
                onPress={() => approveUSDC(bidAmount)}
                isDisabled={isApproving || !isConnected}
                isPending={isApproving}
              >
                {isApproving ? "Approving…" : `Approve ${bidAmount} USDC`}
              </Button>
            </View>
          )}

          {/* Actions */}
          <View flexDirection="row" gap={4} className={styles.bidActions}>
            <Button
              variant={ButtonVariant.Border}
              size={ButtonSize.Large}
              onPress={() => {
                setSelectedItem(null);
                setBidAmount("");
              }}
              flex={1}
              display="flex"
            >
              Cancel
            </Button>
            <Button
              variant={ButtonVariant.CTA}
              size={ButtonSize.Large}
              onPress={createBid}
              isDisabled={
                !bidAmount ||
                isCreatingBid ||
                !isConnected ||
                insufficientBalance ||
                insufficientAllowance
              }
              isPending={isCreatingBid}
              flex={1}
              display="flex"
            >
              {isCreatingBid
                ? "Signing…"
                : activeBidExists
                  ? "Update Bid"
                  : "Create Bid"}
            </Button>
          </View>
        </View>
      )}

      {/* Active bids */}
      <View flexDirection="column" gap={6}>
        <View
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text
            variant={TextVariant.Body2}
            weight={TextWeight.SemiBold}
            color="var(--secondary-cadet-50)"
          >
            Your bids
          </Text>
          <Chip variant={ChipVariant.Neutral}>
            {activeBids.filter((b) => b.status === "active").length} active
          </Chip>
        </View>

        {activeBids.length === 0 ? (
          <View className={styles.emptyBids}>
            <Text variant={TextVariant.Body2} color="var(--secondary-cadet-50)">
              No bids yet. Select an item above to create your first bid.
            </Text>
          </View>
        ) : (
          <View flexDirection="column" gap={3}>
            {activeBids.map((bid, index) => {
              const item = items.find((i) => i.id === bid.itemId);
              if (!item) return null;

              const chipVariant =
                STATUS_CHIP_VARIANT[bid.status] || ChipVariant.Neutral;

              return (
                <View
                  key={`${bid.itemId}-${bid.createdAt}`}
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={4}
                  className={styles.bidRow}
                >
                  <View flexDirection="row" gap={3} alignItems="center">
                    <span className={styles.bidEmoji}>{item.emoji}</span>
                    <View flexDirection="column">
                      <Text
                        variant={TextVariant.Body2}
                        weight={TextWeight.SemiBold}
                        color="var(--primary-navy-90)"
                      >
                        {item.name}
                      </Text>
                      <Text
                        variant={TextVariant.Caption1}
                        color="var(--secondary-cadet-50)"
                      >
                        Bid ${bid.bidAmount} USDC ·{" "}
                        {bid.status === "executed"
                          ? `Paid $${bid.purchasePrice}`
                          : `Current $${item.currentPrice}`}
                      </Text>
                    </View>
                  </View>
                  <View
                    flexDirection="row"
                    gap={3}
                    alignItems="center"
                    className={styles.bidRowActions}
                  >
                    <View flexDirection="column" alignItems="flex-end" gap={1}>
                      <Chip variant={chipVariant}>
                        {bid.status.toUpperCase()}
                      </Chip>
                      {bid.status === "executed" && bid.txHash ? (
                        <a
                          href={txExplorerUrl(bid.txHash)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.txLink}
                        >
                          View TX <ExternalLinkIcon size={10} />
                        </a>
                      ) : (
                        <Text
                          variant={TextVariant.Caption1}
                          color="var(--secondary-cadet-50)"
                        >
                          {formatTime(bid.createdAt)}
                        </Text>
                      )}
                    </View>
                    {bid.status === "active" && (
                      <Button
                        variant={ButtonVariant.Border}
                        size={ButtonSize.Small}
                        onPress={() => cancelBid(index)}
                      >
                        Cancel
                      </Button>
                    )}
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </View>
    </View>
  );
};
